import React, { useEffect, useState } from 'react';
import {
  FaBars,
  FaDumbbell,
  FaMoon,
  FaSearch,
  FaSignOutAlt,
  FaSun,
  FaTimes,
  FaVolumeMute,
  FaVolumeUp,
} from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import {
  adminNavigationSections,
} from './adminNavigationConfig';
import { filterAdminNavigationSections, readStoredAdminUser } from './adminAccessControl';
import { useCheckinNotifications } from './useCheckinNotifications';
import './Navigation.css';

function Navigation() {
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { soundEnabled, toggleSound: handleSoundToggle } = useCheckinNotifications({
    poll: false,
    listenDirect: false,
  });

  const currentUser = readStoredAdminUser();

  const displayName =
    currentUser.full_name ||
    currentUser.name ||
    currentUser.username ||
    currentUser.AdminName ||
    'Admin';
  const displayRole = currentUser.role || currentUser.level || 'Administrator';
  const userInitials = displayName.trim().charAt(0).toUpperCase();

  const isRouteActive = item =>
    item.exact
      ? location.pathname === item.to
      : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
  const filteredNavigation = filterAdminNavigationSections(
    adminNavigationSections,
    currentUser,
    searchQuery
  );

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileOpen) {
      document.body.style.overflow = '';
      return undefined;
    }

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  return (
    <div className={`reference-nav-shell ${isMobileOpen ? 'is-menu-open' : ''}`}>
      <button
        type="button"
        className={`reference-mobile-launcher ${isMobileOpen ? 'is-hidden' : ''}`}
        aria-label="Open menu"
        onClick={() => setIsMobileOpen(true)}
      >
        <FaBars />
        <span>Menu</span>
      </button>

      <button
        type="button"
        className={`reference-mobile-backdrop ${isMobileOpen ? 'is-open' : ''}`}
        aria-label="Close navigation"
        onClick={() => setIsMobileOpen(false)}
      />

      <aside className={`reference-sidebar ${isMobileOpen ? 'is-open' : ''}`}>
        <div className="reference-sidebar__mobile-head">
          <div className="reference-sidebar__mobile-brand">
            <FaDumbbell />
            <span>Primefit Menu</span>
          </div>
          <button
            type="button"
            className="reference-sidebar__mobile-close"
            aria-label="Close menu"
            onClick={() => setIsMobileOpen(false)}
          >
            <FaTimes />
          </button>
        </div>

        <div className="reference-sidebar__user">
          <span className="reference-sidebar__avatar">{userInitials}</span>
          <div>
            <strong>{displayName}</strong>
            <small>{displayRole}</small>
          </div>
        </div>

        <div className="reference-sidebar__search">
          <FaSearch />
          <input
            type="search"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search..."
            aria-label="Search navigation"
          />
          <span>Ctrl K</span>
        </div>

        <div className="reference-sidebar__sections">
          {filteredNavigation.map(section => (
            <div key={section.title} className="reference-sidebar__section">
              <p>{section.title}</p>

              <div className="reference-sidebar__menu">
                {section.items.map(item => {
                  const Icon = item.icon;
                  const isActive = isRouteActive(item);

                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`reference-sidebar__item ${isActive ? 'is-active' : ''}`}
                      onClick={() => setIsMobileOpen(false)}
                    >
                      <Icon />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="reference-sidebar__footer">
          <button
            type="button"
            className="reference-sidebar__utility"
            onClick={handleSoundToggle}
          >
            {soundEnabled ? <FaVolumeUp /> : <FaVolumeMute />}
            <span>{soundEnabled ? 'Voice On' : 'Voice Off'}</span>
          </button>

          <button
            type="button"
            className="reference-sidebar__utility"
            onClick={toggleTheme}
          >
            {isDark ? <FaSun /> : <FaMoon />}
            <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
          </button>

          <Link
            to="/logout"
            className="reference-sidebar__utility reference-sidebar__utility--danger"
            onClick={() => setIsMobileOpen(false)}
          >
            <FaSignOutAlt />
            <span>Logout</span>
          </Link>
        </div>

        <div className="reference-sidebar__brand">
          <FaDumbbell />
          <span>PRIMEFIT</span>
        </div>
      </aside>
    </div>
  );
}

export default Navigation;
